import React from 'react';
import PropTypes from 'prop-types';

import {Box, Paper, Skeleton, Stack, Typography} from 'views/components';
import {Translate, translate} from 'views/module';

import SectionHeading from '../shared/SectionHeading';
import formatters from '../shared/formatters';
import {getThroughDate, PERIOD_LABEL_EXPRESSIONS, PERIOD_ROLES} from '../shared/periods';

const TIMEZONE = 'America/Toronto';

// Compact context strip: names the three calendar periods the snapshot spans, their date
// ranges, and (for the current period) the backend-provided actual cut-off. Every month
// here comes straight from the snapshot, so the browser timezone never decides them.
const SnapshotHeaderSection = (props) => {
  const {
    isLoading,
    snapshot,
  } = props;

  const throughDate = getThroughDate(snapshot);

  const cards = React.useMemo(() => {
    return PERIOD_ROLES.map((role) => {
      const period = snapshot?.periods?.[role] || null;
      const isCurrent = role === 'current';
      const isNext = role === 'next';

      let note = translate('metrics.ClientMetrics.SnapshotHeader.ScheduledOnly');

      if (isCurrent && throughDate) {
        note = translate('metrics.ClientMetrics.SnapshotHeader.ActualThrough', {
          date: formatters.formatThroughDate(throughDate),
        });
      } else if (!isNext) {
        note = null;
      }

      return {
        role,
        labelExpression: PERIOD_LABEL_EXPRESSIONS[role],
        monthLabel: formatters.formatMonthLabel(period?.month),
        rangeText: translate('metrics.ClientMetrics.SnapshotHeader.PeriodRange', {
          startDate: formatters.formatIsoDate(period?.startDate),
          endDate: formatters.formatIsoDate(period?.endDate),
        }),
        note,
      };
    });
  }, [snapshot, throughDate]);

  return (
    <Box>
      <SectionHeading expression='metrics.ClientMetrics.SnapshotHeader.SectionTitle' />
      <Paper
        sx={{
          p: 3,
          borderRadius: '12px',
          border: '1px solid #E5E5E5',
          boxShadow: 'none',
        }}
      >
        {
          isLoading &&
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {xs: '1fr', md: 'repeat(3, minmax(0, 1fr))'},
              gap: 2,
            }}
          >
            {
              PERIOD_ROLES.map((role) => (
                <Stack key={role} spacing={0.75}>
                  <Skeleton variant='text' width='50%' height={16} />
                  <Skeleton variant='text' width='70%' height={24} />
                  <Skeleton variant='text' width='60%' height={16} />
                </Stack>
              ))
            }
          </Box>
        }
        {
          !isLoading &&
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {xs: '1fr', md: 'repeat(3, minmax(0, 1fr))'},
              gap: 2,
            }}
          >
            {
              cards.map((card) => (
                <Stack
                  key={card.role}
                  spacing={0.5}
                  sx={{
                    'borderLeft': {xs: 'none', md: '1px solid #EEE9E1'},
                    'pl': {xs: 0, md: 2},
                    '&:first-of-type': {borderLeft: 'none', pl: 0},
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '11px',
                      fontWeight: 600,
                      letterSpacing: '0.04em',
                      color: '#A8A49E',
                    }}
                  >
                    <Translate expression={card.labelExpression} />
                  </Typography>
                  <Typography sx={{fontSize: '18px', fontWeight: 600, lineHeight: '23px', color: '#1A1916'}}>
                    {card.monthLabel}
                  </Typography>
                  <Typography sx={{fontSize: '12px', lineHeight: '16px', color: '#6B6761'}}>
                    {card.rangeText}
                  </Typography>
                  {
                    card.note &&
                    <Typography sx={{fontSize: '11px', lineHeight: '15px', color: '#A8A49E'}}>
                      {card.note}
                    </Typography>
                  }
                </Stack>
              ))
            }
          </Box>
        }
      </Paper>
      <Typography sx={{fontSize: '11px', lineHeight: '15px', color: '#C0BBB2', mt: 1}}>
        <Translate
          expression='metrics.ClientMetrics.SnapshotHeader.Timezone'
          substitute={{timezone: TIMEZONE}}
        />
      </Typography>
    </Box>
  );
};

SnapshotHeaderSection.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  snapshot: PropTypes.object,
};

export default React.memo(SnapshotHeaderSection);
